import React, { useCallback, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import { Paper } from '@mui/material';
import { useResizeDetector } from 'react-resize-detector';
import { useGetMyUtilizationAnalyticsData } from 'modules/Insights/Notes/hooks/useGetMyUtilizationAnalytics';
import { useNoteInsightsContext } from 'modules/Insights/Notes/NoteInsightsContextProvider';
import UtilizationChart from './UtilizationChart';
import { dataType, transformUtilzationAnalyticsData } from './utils';
import UtilizationChartSkeleton from './UtilizationChartSkeleton';
import UtilizationChartNoData from './UtilizationChartNoData';
import Header from './Header';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  heading: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontSize: theme.spacing(2.6)
  }
}));

const UserUtilizationChart: React.FC = () => {
  const [checked, setChecked] = useState(false);
  const classes = useStyles();
  const [currentChartData, setCurrentChartData] = useState<dataType[]>([]);

  const { data: rData, loading, isEmpty } = useGetMyUtilizationAnalyticsData();
  const { currentPeriod, selectedViewOption } = useNoteInsightsContext();
  const tData = useMemo(
    () => transformUtilzationAnalyticsData(rData, checked, currentPeriod),
    [checked, currentPeriod, rData]
  );

  const handleCheckBoxChange = useCallback((event) => {
    setChecked(event.target.checked);
  }, []);

  const headerNameComponent = useMemo(
    () => (
      <Typography variant="h4" className={classes.heading}>
        <FormattedMessage id="insights.timeUtilizationChart.userUtilizationChartHeading" />
      </Typography>
    ),
    [classes.heading]
  );

  const { width, ref: chartRef } = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 0
  });

  return (
    <Paper
      className={classes.container}
      elevation={3}
      data-testid={'my-utilization-chart-container'}
      ref={chartRef}
      tabIndex={0}
    >
      <Grid container>
        <Header
          currentData={currentChartData}
          headerNameComponent={headerNameComponent}
          checked={checked}
          handleCheckBoxChange={handleCheckBoxChange}
          disabled={loading || isEmpty}
          isEmpty={isEmpty}
          loading={loading}
        />
        {loading ? (
          <UtilizationChartSkeleton />
        ) : isEmpty ? (
          <UtilizationChartNoData isUtilizationSelected={true} />
        ) : (
          <UtilizationChart
            selectedViewOptionSupervisor={selectedViewOption}
            width={width}
            tData={tData}
            setCurrentChartData={setCurrentChartData}
          />
        )}
      </Grid>
    </Paper>
  );
};

export default React.memo(UserUtilizationChart);
